import { motion } from 'framer-motion';
import { useEffect, useState } from 'react';
import {
    FiClock,
    FiEye,
    FiFilter,
    FiMail,
    FiMessageSquare,
    FiPhone,
    FiSearch
} from 'react-icons/fi';
import { MdDirectionsCar } from 'react-icons/md';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { getApiUrl } from '../../config/api.config';

const Enquiries = () => {
  const navigate = useNavigate();
  const [enquiries, setEnquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetchEnquiries();
  }, []);

  const fetchEnquiries = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('agent_token');

      // Fetch chats and car test rides in parallel
      const [chatsRes, carsRes] = await Promise.all([
        fetch(getApiUrl('/chats/agent'), {
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
        }),
        fetch(getApiUrl('/cars'), {
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
        })
      ]);

      const allItems = [];

      // Process chats
      if (chatsRes.ok) {
        const chatsData = await chatsRes.json();
        const chats = chatsData.data || [];
        chats.forEach(chat => {
          const buyer = chat.buyer || {};
          const car = chat.car || {};
          const lastMsg = chat.lastMessage || {};
          const firstMsg = chat.messages?.[0];
          allItems.push({
            id: chat._id,
            type: 'chat',
            buyerName: [buyer.firstName, buyer.lastName].filter(Boolean).join(' ') || 'Unknown Buyer',
            buyerEmail: buyer.email || 'N/A',
            buyerPhone: buyer.phone || buyer.phoneNumber || 'N/A',
            carName: car.title || `${car.brand || ''} ${car.model || ''}`.trim() || 'Unknown Car',
            carId: car._id,
            enquiryType: 'general',
            status: chat.status === 'active' ? 'pending' : 'closed',
            message: lastMsg.text || firstMsg?.text || 'No messages yet',
            messageCount: chat.messages?.length || 0,
            unreadCount: chat.unreadCount || 0,
            createdAt: chat.createdAt,
            isTestRide: false,
            source: 'BuyerApp'
          });
        });
      }

      // Process test rides from cars
      if (carsRes.ok) {
        const carsData = await carsRes.json();
        const cars = carsData.data?.cars || carsData.data || [];
        cars.forEach(car => {
          (car.testRideCustomers || []).forEach((tr, idx) => {
            allItems.push({
              id: `tr-${car._id}-${idx}`,
              type: 'testRide',
              buyerName: tr.name || 'Unknown',
              buyerEmail: tr.email || 'N/A',
              buyerPhone: tr.phone || 'N/A',
              carName: car.title || `${car.brand || ''} ${car.model || ''}`.trim() || 'Unknown Car',
              carId: car._id,
              enquiryType: 'test-ride',
              status: tr.interested ? 'contacted' : 'pending',
              message: tr.feedback || 'Test ride request',
              messageCount: 0,
              unreadCount: 0,
              createdAt: tr.date || car.createdAt,
              isTestRide: true,
              source: tr.source || 'Agent',
              interested: tr.interested
            });
          });
        });
      }

      // Sort by date (newest first)
      allItems.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
      setEnquiries(allItems);
    } catch (error) {
      console.error('Error fetching enquiries:', error);
      toast.error('Failed to load enquiries');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { bg: 'bg-yellow-100', text: 'text-yellow-800', label: 'Pending' },
      contacted: { bg: 'bg-blue-100', text: 'text-blue-800', label: 'Contacted' },
      'test-ride-scheduled': { bg: 'bg-purple-100', text: 'text-purple-800', label: 'Scheduled' },
      closed: { bg: 'bg-green-100', text: 'text-green-800', label: 'Closed' }
    };
    const config = statusConfig[status] || statusConfig.pending;
    return (
      <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${config.bg} ${config.text}`}>
        {config.label}
      </span>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    const now = new Date();
    const diff = now - date;
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (hours < 1) return 'Just now';
    if (hours < 24) return `${hours} hours ago`;
    if (days === 1) return 'Yesterday';
    if (days < 7) return `${days} days ago`;
    
    return date.toLocaleDateString('en-IN', { 
      day: 'numeric', 
      month: 'short', 
      year: 'numeric' 
    });
  };

  const filteredEnquiries = enquiries.filter(enquiry => {
    const matchesFilter = filter === 'all' || 
                         (filter === 'test-ride' && enquiry.isTestRide) ||
                         (filter === 'chat' && enquiry.type === 'chat') ||
                         (filter === 'unread' && enquiry.type === 'chat' && (enquiry.unreadCount || 0) > 0) ||
                         (filter !== 'test-ride' && filter !== 'chat' && enquiry.status === filter);
    const matchesSearch = searchQuery === '' ||
                         enquiry.buyerName.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         enquiry.carName.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         enquiry.buyerEmail.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesFilter && matchesSearch;
  });

  const stats = {
    total: enquiries.length,
    chats: enquiries.filter(e => e.type === 'chat').length,
    testRides: enquiries.filter(e => e.isTestRide).length,
    pending: enquiries.filter(e => e.status === 'pending').length,
    unreadMessages: enquiries.filter(e => e.type === 'chat').reduce((sum, enquiry) => sum + (enquiry.unreadCount || 0), 0)
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="flex flex-col items-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
          <p className="mt-4 text-gray-600">Loading enquiries...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold gradient-text">Enquiries Management</h1>
        <p className="mt-2 text-gray-600">Buyer chat inquiries and test ride bookings for your cars</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-xl p-6 shadow-soft border border-gray-100 hover:shadow-lg transition-shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Enquiries</p>
              <p className="mt-2 text-3xl font-bold text-gray-900">{stats.total}</p>
            </div>
            <div className="p-3 bg-gradient-to-br from-orange-400 to-orange-600 rounded-xl">
              <FiMessageSquare className="w-6 h-6 text-white" />
            </div>
          </div>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
          className="bg-white rounded-xl p-6 shadow-soft border border-gray-100 hover:shadow-lg transition-shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Chat Enquiries</p>
              <p className="mt-2 text-3xl font-bold text-blue-600">{stats.chats}</p>
            </div>
            <div className="p-3 bg-gradient-to-br from-blue-400 to-blue-600 rounded-xl">
              <FiMessageSquare className="w-6 h-6 text-white" />
            </div>
          </div>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
          className="bg-white rounded-xl p-6 shadow-soft border border-gray-100 hover:shadow-lg transition-shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Test Rides</p>
              <p className="mt-2 text-3xl font-bold text-purple-600">{stats.testRides}</p>
            </div>
            <div className="p-3 bg-gradient-to-br from-purple-400 to-purple-600 rounded-xl">
              <MdDirectionsCar className="w-6 h-6 text-white" />
            </div>
          </div>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}
          className="bg-white rounded-xl p-6 shadow-soft border border-gray-100 hover:shadow-lg transition-shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Pending</p>
              <p className="mt-2 text-3xl font-bold text-yellow-600">{stats.pending}</p>
            </div>
            <div className="p-3 bg-gradient-to-br from-yellow-400 to-yellow-600 rounded-xl">
              <FiClock className="w-6 h-6 text-white" />
            </div>
          </div>
        </motion.div>
      </div>

      {/* Filters and Search */}
      <div className="bg-white rounded-xl p-6 shadow-soft border border-gray-100">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="relative flex-1 max-w-md">
            <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search by buyer name, car, or email..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all"
            />
          </div>
          <div className="flex items-center gap-3">
            <FiFilter className="text-gray-400" />
            <select
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className="px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all"
            >
              <option value="all">All Enquiries</option>
              <option value="chat">Chat Enquiries</option>
              <option value="unread">Unread Chats</option>
              <option value="test-ride">Test Rides</option>
              <option value="pending">Pending</option>
              <option value="closed">Closed</option>
            </select>
          </div>
        </div>
      </div>

      {/* Enquiries List */}
      <div className="space-y-4">
        {filteredEnquiries.length === 0 ? (
          <div className="bg-white rounded-xl p-12 text-center shadow-soft border border-gray-100">
            <FiMessageSquare className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No enquiries found</h3>
            <p className="text-gray-600">
              {searchQuery ? 'Try adjusting your search query' : 'No enquiries yet. They will appear when buyers interact from the BuyerApp.'}
            </p>
          </div>
        ) : (
          filteredEnquiries.map((enquiry, index) => (
            <motion.div
              key={enquiry.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
              className={`bg-white rounded-xl p-6 shadow-soft border hover:shadow-lg transition-all cursor-pointer ${
                enquiry.isTestRide ? 'border-purple-200' : 'border-gray-100'
              }`}
              onClick={() => {
                if (enquiry.type === 'chat') {
                  navigate(`/dashboard/enquiries/${enquiry.id}`);
                } else {
                  navigate(`/dashboard/cars/${enquiry.carId}`);
                }
              }}
            >
              <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                {/* Left Section */}
                <div className="flex-1 space-y-3">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      {enquiry.isTestRide ? (
                        <div className="p-2 bg-purple-100 rounded-lg">
                          <MdDirectionsCar className="w-5 h-5 text-purple-600" />
                        </div>
                      ) : (
                        <div className="p-2 bg-blue-100 rounded-lg">
                          <FiMessageSquare className="w-5 h-5 text-blue-600" />
                        </div>
                      )}
                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className="text-lg font-semibold text-gray-900">{enquiry.buyerName}</h3>
                          {enquiry.source === 'BuyerApp' && (
                            <span className="px-2 py-0.5 text-xs font-semibold text-orange-700 bg-orange-100 rounded-full">📱 BuyerApp</span>
                          )}
                        </div>
                        <p className="text-sm text-gray-600">
                          {enquiry.isTestRide ? 'Test ride for: ' : 'Interested in: '}
                          <span className="font-medium text-gray-900">{enquiry.carName}</span>
                        </p>
                      </div>
                    </div>
                    <div className="flex flex-col items-end gap-2">
                      {getStatusBadge(enquiry.status)}
                      {enquiry.isTestRide && (
                        <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-purple-50 text-purple-700">
                          <MdDirectionsCar className="w-3 h-3 mr-1" />
                          Test Ride
                        </span>
                      )}
                      {!enquiry.isTestRide && enquiry.messageCount > 0 && (
                        <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-blue-50 text-blue-700">
                          <FiMessageSquare className="w-3 h-3 mr-1" />
                          {enquiry.messageCount} msg{enquiry.messageCount !== 1 ? 's' : ''}
                        </span>
                      )}
                      {!enquiry.isTestRide && (enquiry.unreadCount || 0) > 0 && (
                        <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-red-50 text-red-700">
                          {enquiry.unreadCount} unread
                        </span>
                      )}
                      {!enquiry.isTestRide && (!enquiry.unreadCount || enquiry.unreadCount === 0) && (
                        <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-emerald-50 text-emerald-700">
                          Read
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Contact Info */}
                  <div className="flex flex-wrap gap-4 text-sm text-gray-600">
                    {enquiry.buyerEmail !== 'N/A' && (
                      <div className="flex items-center gap-2">
                        <FiMail className="w-4 h-4" />
                        <span>{enquiry.buyerEmail}</span>
                      </div>
                    )}
                    <div className="flex items-center gap-2">
                      <FiPhone className="w-4 h-4" />
                      <span>{enquiry.buyerPhone}</span>
                    </div>
                  </div>

                  {/* Message Preview */}
                  <div className="bg-gray-50 rounded-lg p-3">
                    <p className="text-sm text-gray-700 line-clamp-2">{enquiry.message}</p>
                  </div>
                </div>

                {/* Right Section */}
                <div className="flex lg:flex-col items-center lg:items-end gap-3">
                  <div className="text-right">
                    <p className="text-xs text-gray-500">Received</p>
                    <p className="text-sm font-medium text-gray-900">{formatDate(enquiry.createdAt)}</p>
                  </div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      if (enquiry.type === 'chat') {
                        navigate(`/dashboard/enquiries/${enquiry.id}`);
                      } else {
                        navigate(`/dashboard/cars/${enquiry.carId}`);
                      }
                    }}
                    className={`px-4 py-2 text-white rounded-lg transition-all flex items-center gap-2 shadow-md hover:shadow-lg ${
                      enquiry.isTestRide 
                        ? 'bg-gradient-to-r from-purple-500 to-purple-600 hover:from-purple-600 hover:to-purple-700'
                        : 'bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700'
                    }`}
                  >
                    <FiEye className="w-4 h-4" />
                    {enquiry.isTestRide ? 'View Details' : 'View Chat'}
                  </button>
                </div>
              </div>
            </motion.div>
          ))
        )}
      </div>
    </div>
  );
};

export default Enquiries;
